import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import AsyncSelect from 'react-select/async';
import { message } from 'antd';
import styled from 'styled-components';

import {
  useGroupSlackMembersQuery,
  useMapSlackUserMutation,
  GroupSlackMembersDocument,
  PaginatedStudentsDocument,
  PaginatedStudentsQuery,
  PaginatedStudentsQueryVariables,
} from 'core/graphql';
import { Colors } from 'services/ColorService';

interface ISlackMemberMatchPanelProps {
  groupId: string;
}

interface IStudentOption {
  value: string;
  label: string;
}

interface IUnmatchedMember {
  id: string;
  slackUserId: string;
  email: string;
  displayName: string;
}

interface IMemberRowProps {
  member: IUnmatchedMember;
  groupId: string;
  loadStudentOptions: (input: string) => Promise<IStudentOption[]>;
}

/**
 * One unmatched workspace member: identity + a student async-search picker +
 * «Связать». Row-local state keeps one member's selection from leaking into
 * the others.
 */
const MemberRow = memo(
  ({ member, groupId, loadStudentOptions }: IMemberRowProps) => {
    const { t } = useTranslation();
    const [student, setStudent] = useState<IStudentOption | null>(null);

    // Reconcile on the backend re-attaches DM conversations and flips the
    // author's messages to inbound — refresh the group feed + conversation
    // list (by operation name, so every active observer refetches) alongside
    // the member list.
    const [link, { loading }] = useMapSlackUserMutation({
      refetchQueries: [
        {
          query: GroupSlackMembersDocument,
          variables: { groupId, unmatchedOnly: true },
        },
        'GroupMessagesFeed',
        'GroupConversationsList',
      ],
      awaitRefetchQueries: true,
    });

    const onLink = useCallback(async () => {
      if (!student) return;
      const result = await link({
        variables: {
          slackUserId: member.slackUserId,
          studentId: student.value,
          email: member.email || undefined,
          groupId,
        },
      });
      const payload = result.data?.linkSlackUser;
      if (payload?.success) {
        message.success(t('pages.slackConfig.membersLinkSuccess'));
      } else if (payload?.customErrors?.[0]?.message) {
        message.error(payload.customErrors[0].message);
      }
    }, [link, student, member.slackUserId, member.email, groupId, t]);

    return (
      <RowItem>
        <MemberIdentity>
          <MemberName>{member.displayName || member.slackUserId}</MemberName>
          {member.email ? <MemberEmail>{member.email}</MemberEmail> : null}
        </MemberIdentity>
        <PickerBox>
          <AsyncSelect
            cacheOptions
            defaultOptions
            value={student}
            onChange={(v) => setStudent((v as IStudentOption) || null)}
            loadOptions={loadStudentOptions}
            placeholder={t('pages.slackConfig.membersStudentPlaceholder')}
          />
        </PickerBox>
        <LinkButton
          type="button"
          disabled={loading || !student}
          onClick={onLink}
        >
          {t('pages.slackConfig.membersLink')}
        </LinkButton>
      </RowItem>
    );
  },
);

MemberRow.displayName = 'MemberRow';

/**
 * Workspace members without a matched student (workspace-per-group rework).
 * Discovery/sync fills the member list from Slack; the manager pairs each one
 * with a student here so the member's DMs land on the right student card.
 */
export const SlackMemberMatchPanel = memo(
  ({ groupId }: ISlackMemberMatchPanelProps) => {
    const { t } = useTranslation();
    const apollo = useApolloClient();
    const { data } = useGroupSlackMembersQuery({
      variables: { groupId, unmatchedOnly: true },
      fetchPolicy: 'cache-and-network',
    });
    const members = data?.slackWorkspaceMembers || [];

    const loadStudentOptions = useCallback(
      async (input: string): Promise<IStudentOption[]> => {
        try {
          const result = await apollo.query<
            PaginatedStudentsQuery,
            PaginatedStudentsQueryVariables
          >({
            query: PaginatedStudentsDocument,
            variables: { query: input || '', first: 20 },
            fetchPolicy: 'no-cache',
          });
          const edges = result.data?.allStudents?.edges || [];
          return edges
            .map((edge) => edge?.node)
            .filter((node): node is NonNullable<typeof node> => Boolean(node))
            .map((node) => {
              const u = node.user;
              const name =
                [u?.firstName, u?.lastName].filter(Boolean).join(' ') ||
                u?.email ||
                node.id;
              return {
                value: node.id,
                label: `${name}${u?.email ? ` · ${u.email}` : ''}`,
              };
            });
        } catch {
          return [];
        }
      },
      [apollo],
    );

    return (
      <Card>
        <CardTitle>{t('pages.slackConfig.membersTitle')}</CardTitle>
        <Hint>{t('pages.slackConfig.membersHint')}</Hint>
        {members.length === 0 ? (
          <Empty>{t('pages.slackConfig.membersEmpty')}</Empty>
        ) : (
          members.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              groupId={groupId}
              loadStudentOptions={loadStudentOptions}
            />
          ))
        )}
      </Card>
    );
  },
);

SlackMemberMatchPanel.displayName = 'SlackMemberMatchPanel';

const Card = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 10px;
  background: ${Colors.White};
`;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${Colors.Gray};
  margin-bottom: 6px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${Colors.Gray};
  margin-bottom: 10px;
`;

const Empty = styled.div`
  font-size: 12px;
  color: ${Colors.Gray};
`;

const RowItem = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid ${Colors.SecondLightGray};
`;

const MemberIdentity = styled.div`
  flex: 1;
  min-width: 160px;
`;

const MemberName = styled.div`
  font-size: 13px;
  font-weight: 600;
`;

const MemberEmail = styled.div`
  font-size: 12px;
  color: ${Colors.Gray};
`;

const PickerBox = styled.div`
  flex: 2;
  min-width: 220px;
  font-size: 13px;
`;

const LinkButton = styled.button`
  border: 1px solid ${Colors.Blue};
  background: ${Colors.Blue};
  color: ${Colors.White};
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;
